// src/pages/public/SearchPage.jsx
import { useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, SlidersHorizontal, Star, X, ChevronDown } from 'lucide-react'
import { searchApi, fabricSearchApi } from '../../api/services'
import { Button, PageSpinner, Pagination } from '../../components/common/ui'
import toast from 'react-hot-toast'

// ── Constants ─────────────────────────────────────────────────────────────────
const FABRIC_TYPES = ['Cotton','Silk','Polyester','Wool','Linen','Nylon','Rayon','Blended','Denim','Velvet','Chiffon','Georgette','Crepe','Organza','Satin','Brocade']
const WEAVE_TYPES  = ['Plain','Twill','Satin','Jacquard','Knit','Non-woven','Dobby','Waffle','Oxford','Canvas']
const SORT_OPTIONS = [
  { value: '',              label: 'Relevance' },
  { value: 'price_asc',     label: 'Price: Low → High' },
  { value: 'price_desc',    label: 'Price: High → Low' },
  { value: 'newest',        label: 'Newest' },
  { value: 'bestselling',   label: 'Best Selling' },
  { value: 'most_reviewed', label: 'Most Reviewed' },
  { value: 'trending',      label: 'Trending' },
]
const ORIGIN_STATES = ['Andhra Pradesh','Assam','Bihar','Gujarat','Haryana','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Odisha','Punjab','Rajasthan','Tamil Nadu','Telangana','Uttar Pradesh','Uttarakhand','West Bengal']

// ── FilterSection accordion ────────────────────────────────────────────────────
function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-border pb-4 mb-4">
      <button onClick={() => setOpen(o => !o)} className="flex items-center justify-between w-full text-sm font-bold text-gray-800 mb-2">
        {title}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && children}
    </div>
  )
}

// ── Visual search upload panel ────────────────────────────────────────────────
function VisualSearch({ onResults }) {
  const [uploading, setUploading] = useState(false)
  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('image', file)
      const res = await fabricSearchApi.upload(fd)
      onResults(res.data?.data ?? {})
    } catch {
      toast.error('Visual search failed. Please try again.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  return (
    <div className="border border-dashed border-pink rounded-xl p-3 text-center">
      <p className="text-xs font-bold text-gray-700 mb-2">🔍 Search by fabric photo</p>
      <label className="cursor-pointer">
        <span className={`text-xs px-3 py-1.5 rounded-full border border-pink text-pink hover:bg-pink-light transition ${uploading ? 'opacity-60 pointer-events-none' : ''}`}>
          {uploading ? 'Analysing...' : 'Upload photo'}
        </span>
        <input type="file" accept="image/*" onChange={handleFile} className="hidden" disabled={uploading} />
      </label>
    </div>
  )
}

export default function SearchPage() {
  const [sp, setSp]                           = useSearchParams()
  const [showFilters, setShowFilters]         = useState(false)
  const [visualResults, setVisualResults]     = useState(null)

  const q    = sp.get('q') || ''
  const page = parseInt(sp.get('page') || '1')

  const { data, isLoading } = useQuery({
    queryKey: ['search', Object.fromEntries(sp)],
    queryFn:  () => searchApi.search({ ...Object.fromEntries(sp), q, page }),
    enabled:  q.length >= 2,
  })

  const results  = data?.data?.data?.products?.data || data?.data?.data?.data || data?.data?.data?.products || []
  const total    = data?.data?.data?.total || 0
  const lastPage = data?.data?.data?.last_page || 1
  const facets   = data?.data?.data?.facets || {}

  // Param helpers
  const getP  = (k)    => sp.get(k) || ''
  const setP  = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); n.delete('page'); setSp(n) }
  const clearAll = () => setSp(q ? { q } : {})

  // Active filter pills
  const filterKeys = ['fabric_type','weave_type','origin','composition','sort','min_price','max_price','gsm_min','gsm_max','moq_min','moq_max','width_min','width_max','in_stock','gi_tagged']
  const activeFilters = filterKeys.reduce((acc, k) => { const v = sp.get(k); if (v) acc[k] = v; return acc }, {})
  const hasFilters = Object.keys(activeFilters).length > 0

  const filterLabel = (k, v) => {
    const map = {
      fabric_type: `Type: ${v}`, weave_type: `Weave: ${v}`, origin: `Origin: ${v}`,
      composition: `Composition: ${v}`, min_price: `From ₹${v}`, max_price: `To ₹${v}`,
      gsm_min: `GSM ≥ ${v}`, gsm_max: `GSM ≤ ${v}`, moq_min: `MOQ ≥ ${v}m`, moq_max: `MOQ ≤ ${v}m`,
      width_min: `Width ≥ ${v}"`, width_max: `Width ≤ ${v}"`, in_stock: 'In Stock',
      gi_tagged: 'GI Tagged', sort: SORT_OPTIONS.find(s => s.value === v)?.label ?? v,
    }
    return map[k] ?? `${k}: ${v}`
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900">
            {q ? <>Results for <span className="text-pink">"{q}"</span></> : 'Search Fabrics'}
          </h1>
          {total > 0 && <p className="text-sm text-muted mt-1">{total.toLocaleString()} results</p>}
        </div>
        <div className="flex gap-2">
          {/* Sort dropdown */}
          <select
            value={getP('sort')}
            onChange={e => setP('sort', e.target.value)}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:border-pink"
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Button variant="secondary" size="sm" onClick={() => setShowFilters(f => !f)}>
            <SlidersHorizontal size={14} /> Filters
          </Button>
        </div>
      </div>

      {/* Active filter pills */}
      {hasFilters && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(activeFilters).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1 px-3 py-1 bg-pink-light text-pink text-xs rounded-full">
              {filterLabel(k, v)}
              <button onClick={() => setP(k, null)}><X size={10} /></button>
            </span>
          ))}
          <button onClick={clearAll} className="text-xs text-muted hover:text-pink underline">Clear all</button>
        </div>
      )}

      <div className="flex gap-6">
        {/* ── Filter Sidebar ─────────────────────────────────────────── */}
        <aside className={`w-60 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <div className="bg-white border border-border rounded-2xl p-4 sticky top-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-bold text-gray-900">Filters</span>
              {hasFilters && <button onClick={clearAll} className="text-xs text-pink hover:underline">Reset</button>}
            </div>

            {/* Visual search */}
            <div className="mb-4">
              <VisualSearch onResults={(r) => {
                setVisualResults(r)
                toast.success(`Found ${r.match_count ?? 0} matching fabrics`)
              }} />
            </div>

            {/* Price range */}
            <FilterSection title="Price (₹/metre)">
              <div className="flex gap-2">
                <input type="number" placeholder="Min" value={getP('min_price')} onChange={e => setP('min_price', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
                <input type="number" placeholder="Max" value={getP('max_price')} onChange={e => setP('max_price', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
              </div>
            </FilterSection>

            {/* Fabric type */}
            <FilterSection title="Fabric Type">
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {FABRIC_TYPES.map(f => (
                  <label key={f} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:text-pink">
                    <input type="radio" name="fabric_type" value={f} checked={getP('fabric_type') === f}
                      onChange={() => setP('fabric_type', f === getP('fabric_type') ? null : f)}
                      className="accent-pink" />
                    {f}
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Weave type */}
            <FilterSection title="Weave Type">
              <div className="space-y-1">
                {WEAVE_TYPES.map(w => (
                  <label key={w} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer hover:text-pink">
                    <input type="radio" name="weave_type" value={w} checked={getP('weave_type') === w}
                      onChange={() => setP('weave_type', w === getP('weave_type') ? null : w)}
                      className="accent-pink" />
                    {w}
                  </label>
                ))}
              </div>
            </FilterSection>

            {/* Origin state */}
            <FilterSection title="Origin State">
              <select value={getP('origin')} onChange={e => setP('origin', e.target.value)}
                className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink">
                <option value="">All states</option>
                {ORIGIN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </FilterSection>

            {/* Composition */}
            <FilterSection title="Composition">
              <input type="text" placeholder="e.g. cotton, polyester" value={getP('composition')}
                onChange={e => setP('composition', e.target.value)}
                className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
            </FilterSection>

            {/* GSM range */}
            <FilterSection title="GSM Range" defaultOpen={false}>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" value={getP('gsm_min')} onChange={e => setP('gsm_min', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
                <input type="number" placeholder="Max" value={getP('gsm_max')} onChange={e => setP('gsm_max', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
              </div>
            </FilterSection>

            {/* MOQ range */}
            <FilterSection title="Min Order (metres)" defaultOpen={false}>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" value={getP('moq_min')} onChange={e => setP('moq_min', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
                <input type="number" placeholder="Max" value={getP('moq_max')} onChange={e => setP('moq_max', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
              </div>
            </FilterSection>

            {/* Width range */}
            <FilterSection title="Width (inches)" defaultOpen={false}>
              <div className="flex gap-2">
                <input type="number" placeholder="Min" value={getP('width_min')} onChange={e => setP('width_min', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
                <input type="number" placeholder="Max" value={getP('width_max')} onChange={e => setP('width_max', e.target.value)}
                  className="w-full text-xs border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:border-pink" />
              </div>
            </FilterSection>

            {/* Toggles */}
            <FilterSection title="Other" defaultOpen={false}>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer mb-2">
                <input type="checkbox" checked={!!getP('in_stock')} onChange={e => setP('in_stock', e.target.checked ? '1' : null)} className="accent-pink" />
                In Stock Only
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer mb-2">
                <input type="checkbox" checked={!!getP('gi_tagged')} onChange={e => setP('gi_tagged', e.target.checked ? '1' : null)} className="accent-pink" />
                GI Tagged Only
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                <input type="checkbox" checked={!!getP('sample')} onChange={e => setP('sample', e.target.checked ? '1' : null)} className="accent-pink" />
                Sample Available
              </label>
            </FilterSection>
          </div>
        </aside>

        {/* ── Results ───────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0">
          {/* Visual search results banner */}
          {visualResults && visualResults.match_count > 0 && (
            <div className="mb-4 p-3 bg-pink-light rounded-xl border border-pink/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-pink">
                  🎨 Visual match: {visualResults.analysis?.fabric_type ?? 'fabric'} · {visualResults.analysis?.composition ?? ''} · Confidence {Math.round((visualResults.confidence ?? 0) * 100)}%
                </p>
                <button onClick={() => setVisualResults(null)} className="text-muted hover:text-pink"><X size={14} /></button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {(visualResults.matches ?? []).slice(0, 6).map(p => (
                  <Link key={p.id} to={`/fabrics/${p.slug}`} className="group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-white border border-border">
                      <img src={p.primary_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                    <p className="text-[10px] font-bold text-gray-800 truncate mt-1">{p.name}</p>
                    <p className="text-[10px] text-pink">₹{Number(p.price).toLocaleString('en-IN')}/m</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* No query state */}
          {!q && (
            <div className="py-16 text-center">
              <Search size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-muted">Enter a search term to find fabrics</p>
            </div>
          )}

          {isLoading && <PageSpinner />}

          {/* No results */}
          {!isLoading && q && results.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-bold text-gray-900 mb-2">No results for "{q}"</p>
              <p className="text-sm text-muted mb-4">Try different keywords or adjust your filters.</p>
              <Link to="/fabrics"><Button variant="secondary">Browse All Fabrics</Button></Link>
            </div>
          )}

          {/* Product grid */}
          {results.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {results.map(p => (
                <Link key={p.id} to={`/fabrics/${p.slug}`} className="group">
                  <div className="aspect-square rounded-xl overflow-hidden bg-surface border border-border mb-2 group-hover:border-pink transition-colors">
                    <img src={p.primary_image_url || p.primary_image || p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <p className="text-xs font-bold text-gray-900 line-clamp-2">{p.name}</p>
                  <p className="text-xs text-pink font-bold mt-0.5">
                    ₹{Number(p.retail_price || p.price).toLocaleString('en-IN')}/m
                  </p>
                  {p.fabric_type && <p className="text-[10px] text-muted">{p.fabric_type}{p.gsm ? ` · ${p.gsm} GSM` : ''}</p>}
                  {p.average_rating > 0 && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <Star size={10} className="fill-yellow-400 text-yellow-400" />
                      <span className="text-xs text-muted">{p.average_rating}</span>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="mt-8">
              <Pagination currentPage={page} lastPage={lastPage} onPageChange={p => setP('page', p)} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
